import type { BossId } from './types';

/**
 * Boss data (GDD §9.1, issue #89). Every boss shares the arena, phase and defeat rules of the framework in
 * src/core/boss; a concrete boss (JEFE-1 in #95, JEFE-2 in #110, JEFE-3 in #121, JEFE-4 in #133) only adds its
 * `BossDef`: hit points, the phases and the attack pattern each one uses, and optionally its own vertical motion.
 *
 * How to add a boss:
 *  1. write its patterns and register them in src/core/boss/patterns.ts under a `PatternId`;
 *  2. add its `BossDef` to `BOSSES` below, with `phases` from the first (highest `minHp`) to the last;
 *  3. give its level a `boss-arena` segment and `bossId` (the runner then creates the fight by itself).
 */
export type PatternId = string;

export interface BossPhase {
  /** The phase lasts while the boss has at least this many hit points (and fewer than the previous phase). */
  readonly minHp: number;
  readonly pattern: PatternId;
  /** Name of the phase for the hooks and the logs (JEFE-2: HERRADURAS, SALSA, LOS_CABALLEROS). */
  readonly name?: string;
}

export interface VerticalMotion {
  /** Sine between `low` and `high` (u) with this period (s). */
  readonly low: number;
  readonly high: number;
  readonly periodSeconds: number;
}

export interface BossDef {
  readonly id: BossId | 'TEST-BOSS';
  readonly name: string;
  readonly hp: number;
  readonly phases: readonly BossPhase[];
  readonly verticalMotion?: VerticalMotion;
  /** AABB of the body (u); touching it is a damaging collision. */
  readonly hitbox?: { readonly width: number; readonly height: number };
}

/** Where the boss stands on the screen: 80 % of the visible width (GDD §9.1). */
export const BOSS_SCREEN_X = 0.8;
/** Invulnerable transition between phases (seconds, GDD §9.1) and the points for defeating a boss (GDD §12). */
export const BOSS_TRANSITION_S = 1.5;
export const BOSS_DEFEAT_SCORE = 5000;
/** The comic retreat lasts this long before the corridor to the US sky opens (seconds); #96 animates it. */
export const BOSS_RETREAT_S = 3;
/** Default vertical motion (proposal): a sine between y = 3 and y = 10 every 4 s. */
export const DEFAULT_VERTICAL_MOTION: VerticalMotion = { low: 3, high: 10, periodSeconds: 4 };
export const DEFAULT_BOSS_HITBOX = { width: 2, height: 2 } as const;

/** The bosses of the campaign; filled by #95, #110, #121 and #133. */
export const BOSSES: Readonly<Partial<Record<BossId, BossDef>>> = {
  // JEFE-1 El Patrón (GDD §9.2, issue #95): 3 HP, one phase per HP. The displayed name is the catalog key.
  'JEFE-1': {
    id: 'JEFE-1',
    name: 'boss.JEFE-1.name',
    hp: 3,
    phases: [
      { minHp: 3, pattern: 'plata-o-plomo' },
      { minHp: 2, pattern: 'napoles' },
      { minHp: 1, pattern: 'bloque-de-busqueda' },
    ],
  },
  // JEFE-2 El Jinete (GDD §9.2, issue #110): 4 HP; 4-3 HP horseshoes, 2 HP salsa, 1 HP the portrait walls.
  'JEFE-2': {
    id: 'JEFE-2',
    name: 'boss.JEFE-2.name',
    hp: 4,
    // 75 % of the visual size of the model (about 2.9 x 2.9 u): 70 to 80 % on each axis (#111).
    hitbox: { width: 2.2, height: 2.2 },
    phases: [
      { minHp: 3, pattern: 'herraduras', name: 'HERRADURAS' },
      { minHp: 2, pattern: 'salsa', name: 'SALSA' },
      { minHp: 1, pattern: 'los-caballeros', name: 'LOS_CABALLEROS' },
    ],
  },
  // JEFE-3 El Padrinito (GDD §9.2, issue #121): 5 HP; 5-4 HP plazas (chess pieces), 3-2 HP phone calls, 1 HP the check.
  'JEFE-3': {
    id: 'JEFE-3',
    name: 'boss.JEFE-3.name',
    hp: 5,
    // His desk on a hot-air balloon: a body of 2.4 x 2.4 u (the model of #122 is about 3.2 u).
    hitbox: { width: 2.4, height: 2.4 },
    phases: [
      { minHp: 4, pattern: 'plazas', name: 'PLAZAS' },
      { minHp: 2, pattern: 'llamadas', name: 'LLAMADAS' },
      { minHp: 1, pattern: 'jaque', name: 'JAQUE' },
    ],
  },
  // JEFE-4 El Chato (GDD §9.2, issue #133): 6 HP; 6-5 HP Topo, 4-3 HP Moto, 2-1 HP El Muro. He never flies: the tunnels hold him.
  'JEFE-4': {
    id: 'JEFE-4',
    name: 'boss.JEFE-4.name',
    hp: 6,
    hitbox: { width: 2.2, height: 2.2 },
    phases: [
      { minHp: 5, pattern: 'topo', name: 'TOPO' },
      { minHp: 3, pattern: 'moto', name: 'MOTO' },
      { minHp: 1, pattern: 'el-muro', name: 'EL_MURO' },
    ],
  },
};

/**
 * Decoys (GDD §9.1, §7, §12; issue #90): one every 6 s, one on screen at most, 0.8 s of flight, 1 000 points a hit.
 * Spawn band (proposal): y from 2 to 11, in the right third of the view. The plane picks them up with PICKUP_RADIUS.
 */
export const DECOY_SPAWN_S = 6;
export const DECOY_MAX_ON_SCREEN = 1;
export const DECOY_FLIGHT_S = 0.8;
export const DECOY_HIT_SCORE = 1000;
export const DECOY_BAND = { low: 2, high: 11 } as const;
/** The decoy spawns this far right of the left edge of the view, as a fraction of its width (right third). */
export const DECOY_SPAWN_FROM = 2 / 3;
/** Text key of the comic reaction of a hit ("Es harina", GDD §13.4). */
export const DECOY_REACTION_KEY = 'voice.decoy.flour';
/** Text key of the comic reaction of a decoy that arrives while El Chato is in his tunnel (JEFE-4, #133). */
export const DECOY_MISS_KEY = 'voice.decoy.hidden';
